import { useEffect, ReactNode } from 'react';

import { PrevArticle, useCheckoutContext } from '@zooplus/checkout-state';
import { Alert } from '@zooplus/ui-components';

import { useRestoreArticle } from '@hooks/use-restore-article';
import { useTranslation } from '@hooks/use-translations';
import { CART_MESSAGE_TIMEOUT, CHECKOUT_STATE_PROJECT_ID } from '@utilities/constants';
import { getCartMessageActionData, isObjectNotNullOrEmpty } from '@utilities/utils';

type AlertComponentProps = {
  customStyle?: string;
  dataTestId?: string;
  variantType?: 'info' | 'success' | 'error' | 'caution' | 'warning';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  message?: ReactNode;
  showCloseButton?: boolean;
  hideAlert?: boolean;
  isActive?: boolean;
  showActionButton?: boolean;
  setActiveState?: Function;
  buttonActionType?: string;
  couponCode?: string;
};

const AlertComponent = ({
  customStyle,
  dataTestId,
  variantType,
  message,
  showCloseButton,
  hideAlert,
  isActive,
  setActiveState,
  showActionButton,
  buttonActionType,
  couponCode,
}: AlertComponentProps) => {
  const { t } = useTranslation();
  const restoreArticle = useRestoreArticle();

  const { setCoupon, deleteCoupon, prevArticle, setPrevArticle } =
    useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let timeId: any;
    if (hideAlert) {
      timeId = setTimeout(() => {
        setActiveState && setActiveState(false);
        setPrevArticle?.({} as PrevArticle);
      }, CART_MESSAGE_TIMEOUT);
    }

    return () => {
      timeId && clearTimeout(timeId);
    };
  }, [hideAlert, setActiveState, prevArticle]);

  const closeAlertMessage = () => {
    setActiveState && setActiveState(false);
    isObjectNotNullOrEmpty(prevArticle) && setPrevArticle?.({} as PrevArticle);
  };

  const handleRestoreArticle = () => {
    restoreArticle();
    setActiveState && setActiveState(false);
  };

  const handleCouponAddition = () => {
    setCoupon?.({ couponCode: couponCode || '' });
  };

  const handleCouponRemoval = () => {
    couponCode && deleteCoupon?.({ couponCode });
  };

  const { cartMessageActionTestId, label } = getCartMessageActionData(
    t,
    buttonActionType,
    handleCouponAddition,
    handleCouponRemoval,
    handleRestoreArticle,
  );

  return (
    <>
      {showCloseButton && showActionButton ? (
        isActive && (
          <Alert
            className={customStyle}
            data-testid="re-add-article-alert"
            variant={variantType}
            buttonText={t('cart.undo.remove.item.button')}
            onButtonClick={() => handleRestoreArticle()}
            closeButtonAriaLabel="dismiss message"
            onClose={() => closeAlertMessage()}>
            {message}
          </Alert>
        )
      ) : showCloseButton && !showActionButton && !buttonActionType ? (
        isActive && (
          <Alert
            className={customStyle}
            data-testid={dataTestId}
            variant={variantType}
            closeButtonAriaLabel="dismiss message"
            onClose={() => closeAlertMessage()}>
            {message}
          </Alert>
        )
      ) : !showCloseButton && showActionButton && buttonActionType ? (
        <Alert
          className={customStyle}
          data-testid={cartMessageActionTestId}
          variant={variantType}
          onButtonClick={() => handleCouponAddition()}
          buttonText={label}>
          {message}
        </Alert>
      ) : !showCloseButton && !showActionButton ? (
        <Alert className={customStyle} data-testid={dataTestId} variant={variantType}>
          {message}
        </Alert>
      ) : null}
    </>
  );
};

export default AlertComponent;
